import { CorporateEventStatusEnum, VendorPhotoLibraryTypesEnum } from "./enums";
import { EVENT_TYPE_HISTORY } from "../types/eventTypes";
import { REACT_APP_AWS_LAMBDA_API_URL } from "./envConstants";
import { IGetPreviousQuotesParams } from "../types/quoteFormTypes";

const CORE_BACKEND_PREFIX = "/api/core";

export const TERMS_REACCEPTANCE_REQUIRED = "TERMS_REACCEPTANCE_REQUIRED";
export const PASSWORD_CHANGE_REQUIRED = "PASSWORD_CHANGE_REQUIRED";
const AWS_LAMBDA_API_URL = `${REACT_APP_AWS_LAMBDA_API_URL}/default`;

export const API_ENDPOINTS = {
	GET_CORPORATE_INQUIRY_FORM_DATA: "/api/corporate-inquiry-form-data/custom?locale=en&includeInactive=true",
	LOGIN: `${CORE_BACKEND_PREFIX}/v1/auth/login/vendor`,
	GET_USER_PROFILE: (id: string | number) => `${id}`,
	LOGOUT: `${CORE_BACKEND_PREFIX}/v1/auth/logout`,
	EVENT_LIST: (type: number) => `${CORE_BACKEND_PREFIX}/v1/events/vendor/active/${type}`,
	REFRESH_TOKEN: `${CORE_BACKEND_PREFIX}/v1/auth/refresh-auth/vendor`,
	CLOSE_CHAT: `${CORE_BACKEND_PREFIX}/v1/sendbird/freeze-channel`,
	SENDBIRD_SESSION_TOKEN: `${CORE_BACKEND_PREFIX}/v1/sendbird/session-token`,
	CREATE_EVENT_FILTERS: `${CORE_BACKEND_PREFIX}/v1/vendor-filters`,
	EVENT_HISTORY: (type: number) => `${CORE_BACKEND_PREFIX}/v1/events/vendor/history/${type}`,
	EVENT_RESPOND: (eventId: string) => `${CORE_BACKEND_PREFIX}/v1/events/${eventId}/respond-to-event`,
	EVENT_DETAIL: (eventId: string) => `${CORE_BACKEND_PREFIX}/v1/events/${eventId}/details`,
	EVENT_BID: (eventId: string) => `${CORE_BACKEND_PREFIX}/v1/event-bids/v2/${eventId}/vendor`,
	SAVE_EVENT_BID: `${CORE_BACKEND_PREFIX}/v1/event-bids/create-ai-bid`,
	GET_GRID_FILTERS: `${CORE_BACKEND_PREFIX}/v1/vendor-filters/grid`,
	UPLOAD_FILE: `/api/file-upload`,
	UPLOAD_MULTIPLE_FILES: `/api/file-upload/upload-multiple`,
	GET_PDF_MEDIA: (filePath: string) => `/api/file?path=${filePath}`,
	VENDOR_PROFILE_UPDATE: `${CORE_BACKEND_PREFIX}/v1/vendors/update-profile`,
	// Supplier T&Cs (BAQ-1779). Prefix is `vendor` singular — as declared by the
	// Baqaal controller, and as GET_ALL_VENDOR_CHATS below already does. Not a typo.
	TERMS_CURRENT: `${CORE_BACKEND_PREFIX}/v1/vendor/terms/current`,
	TERMS_VERSION: (version: string) =>
		`${CORE_BACKEND_PREFIX}/v1/vendor/terms/${encodeURIComponent(version)}`,
	TERMS_CONTENT: (version: string) =>
		`${CORE_BACKEND_PREFIX}/v1/vendor/terms/${encodeURIComponent(version)}/content`,
	TERMS_ACCEPT: `${CORE_BACKEND_PREFIX}/v1/vendor/terms/accept`,
	TERMS_REMIND: `${CORE_BACKEND_PREFIX}/v1/vendor/terms/remind`,
	NOTIFICATION_REMOVE_ONE: (id: string) => `${CORE_BACKEND_PREFIX}/v1/notifications/remove/${id}`,
	NOTIFICATION_REMOVE_ALL: `${CORE_BACKEND_PREFIX}/v1/notifications/remove`,
	NOTIFICATION_MARK_READ: (id: string) => `${CORE_BACKEND_PREFIX}/v1/notifications/mark-read/${id}`,
	NOTIFICATION_MARK_ALL_READ: `${CORE_BACKEND_PREFIX}/v1/notifications/mark-read/all`,
	NOTIFICATION_COUNTS: `${CORE_BACKEND_PREFIX}/v1/notifications/count`,
	NOTIFICATION_BADGE_COUNT: `${CORE_BACKEND_PREFIX}/v1/notifications/badge/count`,
	GET_NOTIFICATIONS_V2: `${CORE_BACKEND_PREFIX}/v2/notifications`,
	NOTIFICATION_CENTRE_COUNTS: `${CORE_BACKEND_PREFIX}/v2/notifications/counts`,
	EVENT_REQUESTS: `${CORE_BACKEND_PREFIX}/v1/event-requests`,
	EVENT_REQUEST_DECLINE: (eventRequestId: string) =>
		`${CORE_BACKEND_PREFIX}/v1/event-requests/${eventRequestId}/decline`,
	EVENT_REQUEST_ACCEPT: (eventRequestId: string) =>
		`${CORE_BACKEND_PREFIX}/v1/event-requests/${eventRequestId}/accept`,
	EVENT_REQUEST_EXPIRE: (eventRequestId: string) =>
		`${CORE_BACKEND_PREFIX}/v1/event-requests/${eventRequestId}/expire`,
	GET_WHATS_APP_GROUP_INFO: (vendorEventId: string) =>
		`${CORE_BACKEND_PREFIX}/v1/whatsapp-groups/vendorEventId/${vendorEventId}`,
	GET_WHATSAPP_GROUP_LINK: (vendorEventId: string) =>
		`${CORE_BACKEND_PREFIX}/v1/whatsapp-groups/vendorEventId/${vendorEventId}/request-invitation-link`,
	SAVE_DEVICE_TOKEN: `${CORE_BACKEND_PREFIX}/v1/notifications/save-device-token`,
	GET_CORPORATE_EVENTS_LIST: (status: CorporateEventStatusEnum) =>
		`${CORE_BACKEND_PREFIX}/v1/corporate-events/vendor/active/${status}`,
	GET_CORPORATE_EVENT_DETAIL: (id: string) => `${CORE_BACKEND_PREFIX}/v1/corporate-events/${id}/details`,
	GET_CORPORATE_EVENT_WHATS_APP_GROUP_INFO: (corporateEventVendorId: string) =>
		`${CORE_BACKEND_PREFIX}/v1/whatsapp-groups/corporate-event-vendor-id/${corporateEventVendorId}`,
	GET_CORPORATE_EVENT_WHATS_APP_LINK: (corporateEventVendorId: string) =>
		`${CORE_BACKEND_PREFIX}/v1/whatsapp-groups/corporate-event-vendor-id/${corporateEventVendorId}/request-invitation-link`,
	GET_CORPORATE_EVENT_BIDS: (corporateEventId: string) =>
		`${CORE_BACKEND_PREFIX}/v1/corporate-event-bids/${corporateEventId}/vendor`,
	SAVE_CORPORATE_EVENT_BID: `${CORE_BACKEND_PREFIX}/v1/corporate-event-bids`,
	DECLINE_CORPORATE_EVENT: (corporateEventId: string) =>
		`${CORE_BACKEND_PREFIX}/v1/corporate-events/${corporateEventId}/respond-to-event`,
	CORPORATE_EVENT_HISTORY: (type: EVENT_TYPE_HISTORY) =>
		`${CORE_BACKEND_PREFIX}/v1/corporate-events/vendor/history/${type}`,
	UPLOAD_FILE_TO_OPEN_AI: `${AWS_LAMBDA_API_URL}/pdf-compression-lambda`,
	SUBMIT_MENU_EXTRACTION_PROMPT: `${CORE_BACKEND_PREFIX}/v1/corporate-event-bids/menu-extraction-prompt`,
	GET_AI_MENU_AND_SERVICES_INFO: (aiBidsExtractionLogId: string) =>
		`${CORE_BACKEND_PREFIX}/v1/corporate-event-bids/menu-extraction/${aiBidsExtractionLogId}`,
	GET_ALL_SECTIONS: `${CORE_BACKEND_PREFIX}/v1/vendor-photo-library/sections`,
	GET_SECTION_PHOTOS_BY_TYPE: (type: VendorPhotoLibraryTypesEnum) =>
		`${CORE_BACKEND_PREFIX}/v1/vendor-photo-library/sections/by-type/${type}`,
	GET_PHOTOS_BY_SECTION_ID: (sectionId: string) =>
		`${CORE_BACKEND_PREFIX}/v1/vendor-photo-library/sections/${sectionId}`,
	UPDATE_SECTION_NAME: (sectionId: string) => `${CORE_BACKEND_PREFIX}/v1/vendor-photo-library/sections/${sectionId}`,
	DELETE_SECTION: (sectionId: string) => `${CORE_BACKEND_PREFIX}/v1/vendor-photo-library/sections/${sectionId}`,
	ADD_PHOTO_TO_SECTION: (sectionId: string) =>
		`${CORE_BACKEND_PREFIX}/v1/vendor-photo-library/sections/${sectionId}/photos`,
	DELETE_PHOTO_FROM_GALLERY: (photoId: string) => `${CORE_BACKEND_PREFIX}/v1/vendor-photo-library/photos/${photoId}`,
	ADD_NEW_SECTION: `${CORE_BACKEND_PREFIX}/v1/vendor-photo-library/sections/add`,
	UNLINK_PHOTO_FROM_SECTION: (photoId: string) =>
		`${CORE_BACKEND_PREFIX}/v1/vendor-photo-library/photos/${photoId}/unlink-section`,
	ADD_PHOTO_TO_SYSTEM_SECTION: (type: VendorPhotoLibraryTypesEnum) =>
		`${CORE_BACKEND_PREFIX}/v1/vendor-photo-library/vendor/photos/${type}`,

	GET_PREVIOUS_QUOTES: ({ eventType, startDate, endDate }: IGetPreviousQuotesParams) =>
		`${CORE_BACKEND_PREFIX}/v1/vendors/previous-quotes/${eventType}?startDate=${startDate}&endDate=${endDate}`,
	GET_ALL_VENDOR_CHATS: (query?: string) => `${CORE_BACKEND_PREFIX}/v1/vendor/conversations/chats${query ? `?${query}` : ''}`,
  GET_UNREAD_CHANNELS_COUNT: (query?: string) => `${CORE_BACKEND_PREFIX}/v1/vendor/conversations/unread-count${query ? `?${query}` : ''}`,
  ARCHIVE_VENDOR_CHATS: () => `${CORE_BACKEND_PREFIX}/v1/vendor/conversations/chats/archive`,
  UNARCHIVE_VENDOR_CHATS: () => `${CORE_BACKEND_PREFIX}/v1/vendor/conversations/chats/unarchive`,
  GET_ADMIN_VENDOR_CHAT: () => `${CORE_BACKEND_PREFIX}/v1/vendors/support-channel-record`,
  CREATE_ADMIN_VENDOR_CHAT: () => `${CORE_BACKEND_PREFIX}/v1/vendors/create-support-channel`,
	FORGOT_PASSWORD: () => `${CORE_BACKEND_PREFIX}/v1/auth/forgot-password/vendor`,
	RESET_PASSWORD: () => `${CORE_BACKEND_PREFIX}/v1/auth/reset-password/vendor`,
	CHANGE_PASSWORD: `${CORE_BACKEND_PREFIX}/v1/auth/change-password/vendor`,
};
